// The enum editor (spec/dashboard.md section 5.4): name, display name, description, multi-selection, flags and the values
// table; a flags enum refuses a value that is not a power of two (Save disabled, the row named — Q-D-17); Save sends the
// whole value list in one PATCH (or the POST that creates it); the API's `invalid_enum_value` details are shown per
// removed value as bad links to the documents still holding it.
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { ApiError } from '../../api/client';
import { ops } from '../../api/operations';
import type { EnumDefinition } from '../../api/types';
import { displayNameFrom, isPowerOfTwo, nextEnumValue, parameterNameError, templateNameError } from '../../content/schema';
import { routes } from '../../shell/routes';
import { EmptyState } from '../../ui/Banner';
import { Button, IconButton } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { Field } from '../../ui/Field';
import { AlertTriangle, GripVertical, ListTree, Plus, RotateCcw, SlidersHorizontal, Trash2, X } from '../../ui/icons';
import { Checkbox, CommitTextInput, NumberInput, TextArea, TextInput } from '../../ui/inputs';
import { TagInput } from '../../ui/values';
import { fieldErrors, SchemaTabs, useSchemaEnv } from './common';
import { enumUsers } from './EnumsPage';
import './schema.css';

interface Row {
  key: number;
  name: string;
  displayName: string;
  value: number | string;
}

interface EnumCard {
  name: string;
  displayName: string;
  displayNameTouched: boolean;
  description: string;
  isFlags: boolean;
  flags: string[];
  rows: Row[];
}

let rowKey = 0;
function cardOf(e: EnumDefinition | null | undefined): EnumCard {
  if (!e) return { name: '', displayName: '', displayNameTouched: false, description: '', isFlags: false, flags: [], rows: [] };
  return {
    name: e.name,
    displayName: e.displayName ?? '',
    displayNameTouched: true,
    description: e.description ?? '',
    isFlags: e.isFlags,
    flags: [...e.flags],
    rows: e.values.map((v) => ({ key: ++rowKey, name: v.name, displayName: v.displayName ?? '', value: v.value })),
  };
}

const comparable = (c: EnumCard) => JSON.stringify({ ...c, displayNameTouched: true, rows: c.rows.map((r) => ({ name: r.name, displayName: r.displayName, value: String(r.value) })) });

/** The row's problem, named after the row (T20): an invalid name, a duplicate, a value that is not a power of two in a flags enum. */
export function rowError(row: Row, rows: Row[], isFlags: boolean): string | null {
  const label = row.name || 'unnamed value';
  const nameErr = parameterNameError(row.name);
  if (nameErr) return `${label}: ${nameErr}`;
  if (rows.some((r) => r !== row && r.name === row.name)) return `${label}: the name is used twice.`;
  const n = Number(row.value);
  if (!Number.isInteger(n)) return `${label}: the value must be an integer.`;
  if (isFlags && n !== 0 && !isPowerOfTwo(n)) return `${label}: ${n} is not a power of two; a flags enum takes 1, 2, 4, 8… (0 reads "none").`;
  if (!isFlags && rows.some((r) => r !== row && Number(r.value) === n)) return `${label}: the value ${n} is used twice.`;
  return null;
}

/** `'data.rarity' = 3 is not a value of …` → 3. */
function badValueOf(message: string): number | null {
  const m = /=\s*(-?\d+)/.exec(message);
  return m ? Number(m[1]) : null;
}

export function EnumEditorPage() {
  const { enum: routeName } = useParams();
  return <EnumEditor key={routeName ?? '$new'} routeName={routeName ?? null} />;
}

function EnumEditor({ routeName }: { routeName: string | null }) {
  const { client, path, catalog, toasts, confirm, navigate, canWrite, afterWrite, fail, org, gameSlug, branch } = useSchemaEnv();
  const creating = routeName === null;
  const e = creating ? null : (catalog.enums.get(routeName) ?? null);
  const [card, setCard] = useState<EnumCard>(() => cardOf(e));
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [badLinks, setBadLinks] = useState<{ value: number | null; documents: { id: string; field: string | null; message: string }[] }[]>([]);
  const [busy, setBusy] = useState(false);
  const serverCard = useRef<EnumCard>(cardOf(e));
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const serverKey = e ? `${e.name}@${e.version}` : null;
  useEffect(() => {
    if (!creating && e) {
      const fresh = cardOf(e);
      serverCard.current = fresh;
      setCard(fresh);
    }
  }, [serverKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch: Partial<EnumCard>) => setCard((c) => ({ ...c, ...patch }));
  const setRow = (key: number, patch: Partial<Row>) => setCard((c) => ({ ...c, rows: c.rows.map((r) => (r.key === key ? { ...r, ...patch } : r)) }));
  const users = useMemo(() => (creating ? [] : enumUsers(catalog.templateList, routeName)), [catalog.templateList, routeName, creating]);

  const sameNamespace = !!e && e.name.split('.')[0] === card.name.split('.')[0];
  const nameError = templateNameError(card.name, { allowReserved: sameNamespace }) ?? (card.name !== e?.name && catalog.enums.has(card.name) ? `An enum named ${card.name} already exists.` : null);
  const errors = card.rows.map((r) => rowError(r, card.rows, card.isFlags));
  const invalid = !!nameError || errors.some((x) => x !== null);
  const dirty = creating ? card.name.length > 0 : comparable(card) !== comparable(serverCard.current);

  const addValue = () => {
    const value = nextEnumValue(card.rows, card.isFlags);
    let n = card.rows.length;
    let name = `NewValue_${n}`;
    while (card.rows.some((r) => r.name === name)) name = `NewValue_${++n}`;
    set({ rows: [...card.rows, { key: ++rowKey, name, displayName: '', value }] });
  };

  const restoreValue = (value: number) => {
    const old = e?.values.find((v) => Number(v.value) === value);
    if (!old) return;
    set({ rows: [...card.rows, { key: ++rowKey, name: old.name, displayName: old.displayName ?? '', value: old.value }] });
    setBadLinks((list) => list.filter((b) => b.value !== value));
  };

  const save = async () => {
    if (invalid) return;
    setServerErrors({});
    setBadLinks([]);
    const values = card.rows.map((r) => ({ name: r.name, value: Number(r.value), displayName: r.displayName || null }));
    setBusy(true);
    try {
      if (creating) {
        const result = await client.call(ops.createEnum, { path, body: { name: card.name, displayName: card.displayName || null, description: card.description || null, isFlags: card.isFlags, flags: card.flags, values } });
        toasts.success(`Enum ${result.data.name} created.`);
        afterWrite(result.commit);
        navigate(routes.enumEditor(org, gameSlug, branch, result.data.name), { replace: true });
        return;
      }
      if (!e) return;
      const before = serverCard.current;
      const body: Record<string, unknown> = { values };
      if (card.name !== before.name) body.name = card.name;
      if (card.displayName !== before.displayName) body.displayName = card.displayName;
      if (card.description !== before.description) body.description = card.description;
      if (card.isFlags !== before.isFlags) body.isFlags = card.isFlags;
      if (JSON.stringify(card.flags) !== JSON.stringify(before.flags)) body.flags = card.flags;
      const result = await client.call(ops.updateEnum, { path: { ...path, enum: e.name }, ifMatch: Number(e.version), body: body });
      toasts.success(`Enum ${result.data.name} saved.`);
      afterWrite(result.commit);
      if (result.data.name !== e.name) navigate(routes.enumEditor(org, gameSlug, branch, result.data.name), { replace: true });
    } catch (err) {
      const bad = err instanceof ApiError ? (err.details ?? []).filter((d) => d.issue === 'invalid_enum_value') : [];
      if (bad.length > 0) {
        const byValue = new Map<number | null, { id: string; field: string | null; message: string }[]>();
        for (const d of bad) {
          const v = badValueOf(d.message);
          const list = byValue.get(v) ?? [];
          list.push({ id: d.entityId ?? '', field: d.field, message: d.message });
          byValue.set(v, list);
        }
        setBadLinks(Array.from(byValue, ([value, documents]) => ({ value, documents })));
        toasts.error('Documents still hold values this save removes; fix them or restore the values.', { requestId: (err as ApiError).requestId });
      } else {
        setServerErrors(fieldErrors(err));
        fail(err, `Saving ${card.name} failed`);
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!e) return;
    const ok = await confirm({
      title: `Delete enum ${e.displayName ?? e.name}?`,
      body: users.length > 0 ? `${users.length} parameter${users.length === 1 ? '' : 's'} still name this enum; the API refuses the delete until they are changed.` : 'No parameter uses it. The deletion is a commit of its own.',
      typeToConfirm: users.length > 0 ? e.name : undefined,
      confirmLabel: 'Delete enum',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      const result = await client.call(ops.deleteEnum, { path: { ...path, enum: e.name }, ifMatch: Number(e.version) });
      toasts.success(`Enum ${e.name} deleted.`);
      afterWrite(result.commit ?? result.data?.commit);
      navigate(routes.enums(org, gameSlug, branch));
    } catch (err) {
      fail(err, `Deleting ${e.name} failed`);
    }
  };

  /** the template a bad link's document most likely belongs to: the user parameter named by the detail's field */
  const templateOfDetail = (field: string | null): string | null => {
    const last = field?.split('.').pop()?.replace(/\[\d+\]$/, '');
    return (users.find((u) => u.parameter === last) ?? users[0])?.template.name ?? null;
  };

  if (!creating && !e) {
    return (
      <section className="schema-page">
        <div className="page-head">
          <h1>Data structure</h1>
        </div>
        <SchemaTabs />
        <div className="pane">
          {catalog.loading ? (
            <div className="skeleton-rows" role="status" aria-label="Loading">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="skeleton-row">
                  <span className="skeleton" />
                  <span className="skeleton" />
                </div>
              ))}
            </div>
          ) : (
            <div className="pane-body" role="alert">
              <EmptyState
                icon={<ListTree />}
                title={`No enum named ${routeName} on this branch`}
                body="It may have been renamed or deleted, or it lives on another branch."
                action={
                  <Link className="btn btn-s" to={routes.enums(org, gameSlug, branch)}>
                    All enums
                  </Link>
                }
              />
            </div>
          )}
        </div>
      </section>
    );
  }

  const onDragEnd = (ev: DragEndEvent) => {
    if (!ev.over || ev.active.id === ev.over.id) return;
    const from = card.rows.findIndex((r) => r.key === Number(ev.active.id));
    const to = card.rows.findIndex((r) => r.key === Number(ev.over!.id));
    if (from < 0 || to < 0) return;
    set({ rows: arrayMove(card.rows, from, to) });
  };

  return (
    <section className="schema-page">
      <div className="page-head">
        <h1>{creating ? 'New enum' : (e!.displayName ?? e!.name)}</h1>
        {e && <code>{e.name}</code>}
        {e?.isFlags && <Badge tone="info">Flags</Badge>}
        <span className="spacer" />
        {e && (
          <Button variant="danger" onClick={() => void remove()} disabled={!canWrite} icon={<Trash2 aria-hidden="true" />}>
            Delete enum
          </Button>
        )}
      </div>
      <SchemaTabs />
      <div className="schema-editor">
        <form
          className="pane schema-card"
          aria-label="Enum"
          onSubmit={(ev) => {
            ev.preventDefault();
            void save();
          }}
        >
          <div className="pane-head">
            <h2>Enum</h2>
            <span className="muted small">Its name, how it reads and whether a value can combine several</span>
          </div>
          <div className="pane-body form-grid">
            <Field label="Name" required error={nameError ?? serverErrors.name}>
              <TextInput className="mono" value={card.name} disabled={!canWrite} autoComplete="off" spellCheck={false} onChange={(ev) => set({ name: ev.target.value, ...(card.displayNameTouched ? {} : { displayName: displayNameFrom(ev.target.value) }) })} />
            </Field>
            <Field label="Display name" error={serverErrors.displayName}>
              <TextInput value={card.displayName} disabled={!canWrite} onChange={(ev) => set({ displayName: ev.target.value, displayNameTouched: true })} />
            </Field>
            <Field className="span-2" label="Description">
              <TextArea rows={2} value={card.description} disabled={!canWrite} onChange={(ev) => set({ description: ev.target.value })} />
            </Field>
            <Field label="Flags" help="The enum's own flags, kept as written.">
              <TagInput value={card.flags} onChange={(flags) => set({ flags })} disabled={!canWrite} label="Enum flags" />
            </Field>
            <div className="field check-field">
              <Checkbox label="Multi-selection (flags)" checked={card.isFlags} disabled={!canWrite} onChange={(ev) => set({ isFlags: ev.target.checked })} />
              <span className="field-help">A value then combines several; each value is a power of two.</span>
            </div>
          </div>
          <div className="pane-foot">
            {invalid && card.rows.length > 0 && <span className="field-error">Fix the values marked in the list first.</span>}
            <span className="spacer" />
            <Button onClick={() => setCard(creating ? cardOf(null) : serverCard.current)} disabled={!dirty || busy}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" busy={busy} disabled={!dirty || invalid || !canWrite} title={invalid ? 'Fix the values marked in the table first.' : undefined}>
              {creating ? 'Create enum' : 'Save'}
            </Button>
          </div>
        </form>

        <section className="pane" aria-label="Values">
          <div className="pane-head">
            <h2>Values</h2>
            <span className="chip">{card.rows.length}</span>
            <span className="spacer" />
            <Button size="s" onClick={addValue} disabled={!canWrite} icon={<Plus aria-hidden="true" />}>
              Add value
            </Button>
          </div>
          {card.rows.length === 0 ? (
            <div className="pane-body">
              <EmptyState
                icon={<ListTree />}
                title="No values yet"
                body="A value is a name and the integer the game stores."
                action={
                  canWrite ? (
                    <Button size="s" onClick={addValue} icon={<Plus aria-hidden="true" />}>
                      Add the first value
                    </Button>
                  ) : undefined
                }
              />
            </div>
          ) : (
            <div className={`enum-values ${card.isFlags ? 'with-bit' : ''}`}>
              <div className="enum-row enum-head" aria-hidden="true">
                <span />
                <span>Name</span>
                <span>Display name</span>
                <span className="num-col">Value</span>
                {card.isFlags && <span>Bit</span>}
                <span />
              </div>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={card.rows.map((r) => String(r.key))} strategy={verticalListSortingStrategy}>
                  <ol className="enum-list">
                    {card.rows.map((r, i) => (
                      <EnumValueRow key={r.key} row={r} index={i} error={errors[i] ?? null} isFlags={card.isFlags} canWrite={canWrite} onChange={(patch) => setRow(r.key, patch)} onRemove={() => set({ rows: card.rows.filter((x) => x.key !== r.key) })} />
                    ))}
                  </ol>
                </SortableContext>
              </DndContext>
            </div>
          )}
        </section>

        {badLinks.length > 0 && (
          <section className="pane bad-links" aria-label="Bad links">
            <div className="pane-head">
              <AlertTriangle className="icon tone-warning" aria-hidden="true" />
              <h2>Bad links</h2>
              <span className="muted small">The save is refused until these documents hold another value or the value is restored.</span>
            </div>
            <div className="pane-body bad-link-groups">
              {badLinks.map((b) => {
                const old = b.value === null ? undefined : e?.values.find((v) => Number(v.value) === b.value);
                return (
                  <div key={String(b.value)} className="bad-link-group">
                    <div className="row">
                      <strong>{old ? `${old.name} (${b.value})` : b.value === null ? 'Values' : String(b.value)}</strong>
                      <span className="chip chip-warning">
                        {b.documents.length} document{b.documents.length === 1 ? '' : 's'}
                      </span>
                      <span className="spacer" />
                      {old && (
                        <Button size="s" onClick={() => restoreValue(b.value!)} icon={<RotateCcw aria-hidden="true" />}>
                          Restore value
                        </Button>
                      )}
                    </div>
                    <ul className="bad-link-list">
                      {b.documents.map((d) => {
                        const tpl = templateOfDetail(d.field);
                        return (
                          <li key={d.id + (d.field ?? '')}>
                            {tpl && d.id ? <Link className="mono" to={`${routes.documents(org, gameSlug, branch, tpl)}?doc=${encodeURIComponent(d.id)}`}>{d.id.slice(0, 8)}</Link> : <code>{d.id || '?'}</code>} <span className="muted small">{d.message}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {!creating && (
          <section className="pane" aria-label="Used by">
            <div className="pane-head">
              <h2>Used by</h2>
              <span className="chip">{users.length}</span>
            </div>
            {users.length === 0 ? (
              <p className="pane-body muted small">No parameter names this enum.</p>
            ) : (
              <div className="list-pane">
                {users.map((u) => (
                  <div key={`${u.template.name}.${u.parameter}`} className="list-row static">
                    <SlidersHorizontal className="icon muted" aria-hidden="true" />
                    <span className="text">
                      <span className="title">
                        <Link to={`${routes.template(org, gameSlug, branch, u.template.name)}?param=${encodeURIComponent(u.parameter)}`}>
                          {u.template.displayName ?? u.template.name} › {u.parameter}
                        </Link>
                      </span>
                    </span>
                    {u.list && <span className="chip">list</span>}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </section>
  );
}

function EnumValueRow({ row: r, index: i, error: err, isFlags, canWrite, onChange, onRemove }: { row: Row; index: number; error: string | null; isFlags: boolean; canWrite: boolean; onChange: (patch: Partial<Row>) => void; onRemove: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: String(r.key), disabled: !canWrite });
  const n = Number(r.value);
  return (
    <li ref={setNodeRef} data-testid={`value-${r.name}`} className={`enum-item ${err ? 'invalid' : ''} ${isDragging ? 'dragging' : ''}`} style={{ transform: CSS.Transform.toString(transform), transition }}>
      <div className="enum-row">
        <button type="button" className="drag-handle" aria-label={`Move ${r.name || `value ${i + 1}`}`} disabled={!canWrite} {...attributes} {...listeners}>
          <GripVertical aria-hidden="true" />
        </button>
        <CommitTextInput className="mono" size="s" value={r.name} disabled={!canWrite} aria-label={`Name of value ${i + 1}`} onCommit={(v) => onChange({ name: v })} />
        <CommitTextInput size="s" value={r.displayName} disabled={!canWrite} aria-label={`Display name of ${r.name}`} onCommit={(v) => onChange({ displayName: v })} />
        <NumberInput className="num-col" size="s" kind="int" value={r.value} disabled={!canWrite} aria-label={`Value of ${r.name}`} aria-invalid={err ? true : undefined} onCommit={(v) => onChange({ value: v ?? 0 })} />
        {isFlags && <span className="bit">{n === 0 ? <span className="chip">none</span> : isPowerOfTwo(n) ? <span className="chip">{`bit ${Math.log2(n)}`}</span> : <span className="muted">—</span>}</span>}
        <IconButton label={`Delete value ${r.name}`} disabled={!canWrite} onClick={onRemove}>
          <X aria-hidden="true" />
        </IconButton>
      </div>
      {err && (
        <div className="field-error enum-error" role="alert">
          {err}
        </div>
      )}
    </li>
  );
}
